import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { ref, computed } from 'vue';
import { getSdk } from '@/composables/useSdk';
import { useCapacitor } from './useCapacitor';
import router from '@/router';

/**
 * Composable for push notification management
 * Handles registration, token management, and notification handling
 *
 * Note: Uses direct router import instead of useRouter() because this composable
 * is called from main.ts after app.mount(), outside component context.
 */
export function usePushNotifications() {
  const { isNative } = useCapacitor();

  const isInitialized = ref(false);
  const registrationToken = ref<string | null>(null);
  const permissionStatus = ref<{ receive: string; default?: string; denied?: boolean }>({ receive: 'prompt', default: 'prompt' });
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Request push notification permissions
   */
  async function requestPermissions() {
    if (!isNative) {
      console.log('[PushNotifications] Not on native platform, skipping');
      return { receive: 'denied' };
    }

    try {
      isLoading.value = true;
      error.value = null;
      const permStatus = await PushNotifications.requestPermissions();
      permissionStatus.value = { 
        receive: permStatus.receive, 
        default: permStatus.receive,
        denied: permStatus.receive === 'denied'
      };
      return permStatus;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to request permissions';
      console.error('[PushNotifications] Error requesting permissions:', err);
      error.value = errorMsg;
      permissionStatus.value = { receive: 'denied', default: 'denied', denied: true };
      return { receive: 'denied' };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Register for push notifications
   */
  async function register() {
    if (!isNative) {
      console.log('[PushNotifications] Not on native platform, skipping registration');
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;
      await PushNotifications.register();
      console.log('[PushNotifications] Registration initiated');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to register';
      console.error('[PushNotifications] Registration error:', err);
      error.value = errorMsg;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Send registration token to backend
   */
  async function registerTokenWithBackend(token: string) {
    try {
      const platform = Capacitor.getPlatform() as 'ios' | 'android';
      const sdk = getSdk();
      await sdk.pushNotificationRegister(token, platform);
      console.log('[PushNotifications] Token registered with backend');
    } catch (err) {
      console.error('[PushNotifications] Failed to register token with backend:', err);
    }
  }

  /**
   * Setup push notification listeners
   */
  function setupListeners() {
    if (!isNative) return;

    // Listen for successful registration
    PushNotifications.addListener('registration', async (token) => {
      console.log('[PushNotifications] Registration success, token:', token.value);
      registrationToken.value = token.value;
      await registerTokenWithBackend(token.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushNotifications] Registration error:', error);
    });

    // Listen for push notifications received (foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[PushNotifications] Push received:', notification);
      // Show in-app notification or handle as needed
      // The notification will appear in notification center automatically
    });

    // Handle notification actions (when user taps notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('[PushNotifications] Push action performed:', notification);
      const data = notification.notification.data;
      
      // Navigate to content if URL is provided
      if (data?.url) {
        router.push(data.url);
      } else if (data?.post_ulid) {
        router.push(`/post/${data.post_ulid}`);
      } else if (data?.user_ulid) {
        router.push(`/profile/${data.username || data.user_ulid}`);
      }
    });
  }

  /**
   * Initialize push notifications
   * Call this on app startup
   */
  async function initialize() {
    if (!isNative || isInitialized.value) {
      return;
    }

    // Setup listeners first
    setupListeners();

    // Request permissions
    const permStatus = await requestPermissions();
    
    if (permStatus.receive === 'granted') {
      // Register for push notifications
      await register();
      isInitialized.value = true;
    } else {
      console.log('[PushNotifications] Permission denied');
    }
  }

  /**
   * Enable notifications (request permissions and register)
   */
  async function enableNotifications(): Promise<boolean> {
    const permStatus = await requestPermissions();
    if (permStatus.receive === 'granted') {
      await register();
      return true;
    }
    return false;
  }

  /**
   * Disable notifications (unregister)
   */
  async function disableNotifications(): Promise<void> {
    // Note: Capacitor doesn't have a direct unregister method
    // This would typically be handled by the backend removing the token
    registrationToken.value = null;
    isInitialized.value = false;
  }

  /**
   * Get current registration token
   */
  function getToken(): string | null {
    return registrationToken.value;
  }

  /**
   * Check if push notifications are supported
   */
  const isSupported = computed(() => isNative);

  /**
   * Check if user can enable notifications (permission is prompt/default)
   */
  const canEnable = computed(() => {
    return permissionStatus.value.receive === 'prompt' || permissionStatus.value.receive === 'prompt';
  });

  /**
   * Check if user is subscribed (has token)
   */
  const isSubscribed = computed(() => {
    return registrationToken.value !== null;
  });

  return {
    initialize,
    requestPermissions,
    register,
    enableNotifications,
    disableNotifications,
    getToken,
    isSupported,
    isInitialized: computed(() => isInitialized.value),
    permissionStatus,
    canEnable,
    isSubscribed,
    isLoading,
    error
  };
}
